/**
 * Maps Apollo / fetch network failures on file uploads to plain-language copy.
 * HTTP 413 from API Gateway or proxies happens before GraphQL runs; surface that here on the client.
 */

const PAYLOAD_TOO_LARGE_HINT =
  'The file could not be uploaded. It may be too large; try a smaller or shorter file.';

const DEFAULT_FALLBACK = 'The file could not be uploaded. Please try again.';

const getNetworkErrorStatusCode = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const networkError = (error as { networkError?: unknown }).networkError;
  if (!networkError || typeof networkError !== 'object') {
    return undefined;
  }

  const statusCode = (networkError as { statusCode?: unknown }).statusCode;
  if (typeof statusCode === 'number') {
    return statusCode;
  }

  const status = (networkError as { status?: unknown }).status;
  if (typeof status === 'number') {
    return status;
  }

  return undefined;
};

const getErrorMessageString = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return '';
};

const isPayloadTooLarge = (error: unknown): boolean => {
  if (getNetworkErrorStatusCode(error) === 413) {
    return true;
  }

  // Some link errors expose status on the thrown object (e.g. ServerError), not only under networkError.
  if (
    error &&
    typeof error === 'object' &&
    (error as { statusCode?: unknown }).statusCode === 413
  ) {
    return true;
  }

  const message = getErrorMessageString(error).toLowerCase();
  if (!message) {
    return false;
  }

  return (
    message.includes('status code 413') ||
    message.includes('payload too large') ||
    message.includes('request entity too large')
  );
};

/** True when the error indicates the HTTP body was rejected as too large (e.g. 413). */
export function isUploadPayloadTooLargeNetworkError(error: unknown): boolean {
  return isPayloadTooLarge(error);
}

/**
 * @param error — Typically an ApolloError or ServerError from a failed upload mutation.
 * @param fallbackMessage — Shown when the error is not recognised as payload-too-large.
 */
export const getFriendlyUploadNetworkErrorMessage = (
  error: unknown,
  fallbackMessage: string = DEFAULT_FALLBACK,
): string => {
  if (isPayloadTooLarge(error)) {
    return PAYLOAD_TOO_LARGE_HINT;
  }
  return fallbackMessage;
};
